import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Folder as FolderIcon } from 'lucide-react';
import { href, Link, useLocation } from '../../../app/router';
import type { Project } from '../../../lib/api';
import { Button, IconButton } from '../../../ui';
import { cn } from '../../../ui/cn';
import { focusRing } from '../../../ui/focus';
import { Icon } from '../../../ui/icons';
import { dropActive } from '../cards';
import { useFolders } from '../queries';

const KEY = 'os.sidebar.expanded';

function readExpanded(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

const row = cn(
  'flex min-w-0 flex-1 items-center gap-2 truncate rounded-md px-2 py-1 font-sans text-sm text-fg-muted hover:bg-surface-2 hover:text-fg aria-[current=page]:bg-surface-2 aria-[current=page]:text-fg pointer-coarse:min-h-11',
  focusRing,
);

function Folders({ project }: { project: Project }) {
  const { path } = useLocation();
  const folders = useFolders(project.id);
  return (
    <ul className="ml-5 flex flex-col">
      {folders.data?.map((f) => {
        const to = href('folder', { projectId: project.id, folderId: f.id });
        return (
          <li key={f.id} className={cn('rounded-md', dropActive)} data-drop-project={project.id} data-drop-folder={f.id} data-drop-archived={project.archivedAt ? 'true' : undefined}>
            <Link to={to} aria-current={path === to ? 'page' : undefined} className={row}>
              <Icon icon={FolderIcon} size={14} />
              <span className="truncate" title={f.name}>
                {f.name}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** How many projects the tree shows before "All projects": the sidebar stays short and quick to tab through. */
export const TREE_LIMIT = 8;

/**
 * Projects of the organization with their folders (P16-4): the first ones in the manual order (the open project
 * always), expandable, remembered, and drop targets for boards.
 */
export function ProjectTree({ projects }: { projects: readonly Project[] }) {
  const { t } = useTranslation();
  const { path } = useLocation();
  const [expanded, setExpanded] = useState(readExpanded);
  const [all, setAll] = useState(false);
  const current = projects.find((p) => path.startsWith(href('project', { projectId: p.id })));
  const head = projects.slice(0, TREE_LIMIT);
  const shown = all ? projects : current && !head.includes(current) ? [...head, current] : head;
  const toggle = (id: string) => {
    const next = expanded.includes(id) ? expanded.filter((x) => x !== id) : [...expanded, id];
    setExpanded(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Blocked storage: remembered for this visit only.
    }
  };
  return (
    <ul className="flex flex-col">
      {shown.map((p) => {
        const open = expanded.includes(p.id);
        const to = href('project', { projectId: p.id });
        return (
          <li key={p.id}>
            <div className={cn('flex items-center rounded-md', dropActive)} data-drop-project={p.id} data-drop-archived={p.archivedAt ? 'true' : undefined}>
              <IconButton
                size="sm"
                aria-expanded={open}
                label={t(open ? 'dashboard.sidebar.hideFolders' : 'dashboard.sidebar.showFolders', { name: p.name })}
                icon={<Icon icon={ChevronRight} size={14} className={cn('transition-transform duration-(--duration-fast)', open && 'rotate-90')} />}
                onClick={() => toggle(p.id)}
                className="h-6 w-6 text-fg-subtle"
              />
              <Link to={to} title={p.name} aria-current={path === to ? 'page' : undefined} className={cn(row, p.archivedAt && 'opacity-60')}>
                {p.name}
              </Link>
            </div>
            {open && <Folders project={p} />}
          </li>
        );
      })}
      {projects.length > TREE_LIMIT && (
        <li>
          <Button variant="ghost" size="sm" className="w-full justify-start text-fg-muted" onClick={() => setAll(!all)}>
            {all ? t('dashboard.sidebar.fewerProjects') : t('dashboard.sidebar.allProjects', { count: projects.length })}
          </Button>
        </li>
      )}
    </ul>
  );
}
